import { BufferAttribute, BufferGeometry, Vector3 } from 'three';

export interface RampOptions {
  /** Run length along +Z, in units. */
  length: number;
  /** Width along X, in units. */
  width: number;
  /** Slope pitch in degrees (30-60 for typical surf ramps). */
  angleDeg: number;
  /**
   * Include the flat end caps (default true). Lane segments chained by
   * buildLane MUST omit caps: on the inside of a turn a cap protrudes through
   * the neighbouring face as a backward-facing wall that dead-stops riders.
   */
  caps?: boolean;
}

/** Outward normal of a ramp's sloped face (ascending toward +Z). */
export function rampSurfaceNormal(angleDeg: number): Vector3 {
  const a = (angleDeg * Math.PI) / 180;
  return new Vector3(0, Math.cos(a), -Math.sin(a));
}

/**
 * Right-triangle wedge: flat at z=0, rising to height = length * tan(angle)
 * at z=length. Origin at the center of the bottom front edge. Non-indexed
 * triangles with flat normals — surf collision depends on exact face normals.
 */
export function makeRamp({ length, width, angleDeg }: RampOptions): BufferGeometry {
  const h = length * Math.tan((angleDeg * Math.PI) / 180);
  const w = width / 2;
  const l = length;

  const a: [number, number, number] = [-w, 0, 0]; // front bottom left
  const b: [number, number, number] = [w, 0, 0]; // front bottom right
  const c: [number, number, number] = [w, 0, l]; // back bottom right
  const d: [number, number, number] = [-w, 0, l]; // back bottom left
  const e: [number, number, number] = [w, h, l]; // back top right
  const f: [number, number, number] = [-w, h, l]; // back top left

  // CCW winding viewed from outside each face.
  const faces: [number, number, number][][] = [
    [a, b, c],
    [a, c, d], // bottom (-Y)
    [a, e, b],
    [a, f, e], // slope (+Y/-Z)
    [c, e, f],
    [c, f, d], // back (+Z)
    [b, e, c], // right cap (+X)
    [a, d, f], // left cap (-X)
  ];

  return geometryFromFaces(faces);
}

/**
 * Classic CS-style surf ramp: a triangular prism (peaked cross-section)
 * extruded along +Z. Players ride the two sloped side faces. angleDeg is the
 * side face's pitch from horizontal — use > 50 so normal.y < SURF_NORMAL_Y.
 */
export function makeSurfPrism({
  length,
  width,
  angleDeg,
  caps = true,
}: RampOptions): BufferGeometry {
  const w = width / 2;
  const h = w * Math.tan((angleDeg * Math.PI) / 180);
  const l = length;

  const a0: [number, number, number] = [-w, 0, 0]; // base left, front
  const b0: [number, number, number] = [w, 0, 0]; // base right, front
  const t0: [number, number, number] = [0, h, 0]; // apex, front
  const a1: [number, number, number] = [-w, 0, l];
  const b1: [number, number, number] = [w, 0, l];
  const t1: [number, number, number] = [0, h, l];

  const faces: [number, number, number][][] = [
    [b0, t0, t1],
    [b0, t1, b1], // right slope (+X-ish normal)
    [a0, t1, t0],
    [a0, a1, t1], // left slope (-X-ish normal)
    [a0, b0, b1],
    [a0, b1, a1], // bottom (-Y)
  ];
  if (caps) {
    faces.push(
      [a0, t0, b0], // front cap (-Z)
      [a1, b1, t1], // back cap (+Z)
    );
  }

  return geometryFromFaces(faces);
}

/** Outward normal of a surf prism's right slope face. */
export function surfPrismSideNormal(angleDeg: number): Vector3 {
  const a = (angleDeg * Math.PI) / 180;
  return new Vector3(Math.sin(a), Math.cos(a), 0);
}

/**
 * One-sided wall ramp: a right-triangle cross-section extruded along +Z. The
 * riding face rises from the foot line (x=0) toward +x, pitched `angleDeg`
 * from horizontal; the back at x = height/tan(angle) is vertical. Mirror with
 * rotation.y = PI to face the other way. Used for V-valleys and half-pipes.
 */
export function makeWallRamp({
  length,
  height,
  angleDeg,
  caps = true,
}: {
  length: number;
  height: number;
  angleDeg: number;
  caps?: boolean;
}): BufferGeometry {
  const w = height / Math.tan((angleDeg * Math.PI) / 180);
  const l = length;
  const f0: [number, number, number] = [0, 0, 0]; // foot, front
  const f1: [number, number, number] = [0, 0, l]; // foot, back
  const t0: [number, number, number] = [w, height, 0]; // top, front
  const t1: [number, number, number] = [w, height, l]; // top, back
  const b0: [number, number, number] = [w, 0, 0]; // base under top, front
  const b1: [number, number, number] = [w, 0, l]; // base under top, back

  const faces: [number, number, number][][] = [
    [f0, t1, t0],
    [f0, f1, t1], // riding face (normal -x/+y)
    [b0, t1, b1],
    [b0, t0, t1], // back (+x)
    [f0, b1, f1],
    [f0, b0, b1], // bottom (-y)
  ];
  if (caps) {
    faces.push([f0, t0, b0], [f1, b1, t1]);
  }
  return geometryFromFaces(faces);
}

/**
 * Spire: a truncated cone shell to wrap around helically (classic surf
 * obstacle, see MAP_DESIGN.md). Face pitch from horizontal must land in the
 * surfable band: atan(height / (baseRadius - topRadius)) — keep normal.y
 * between ~0.2 and 0.7. Flat-shaded facets, no end disks.
 */
export function makeSpire({
  baseRadius,
  topRadius,
  height,
  sides = 24,
}: {
  baseRadius: number;
  topRadius: number;
  height: number;
  sides?: number;
}): BufferGeometry {
  const faces: [number, number, number][][] = [];
  for (let i = 0; i < sides; i++) {
    const a0 = (i / sides) * Math.PI * 2;
    const a1 = ((i + 1) / sides) * Math.PI * 2;
    const lo0: [number, number, number] = [Math.cos(a0) * baseRadius, 0, Math.sin(a0) * baseRadius];
    const lo1: [number, number, number] = [Math.cos(a1) * baseRadius, 0, Math.sin(a1) * baseRadius];
    const hi0: [number, number, number] = [
      Math.cos(a0) * topRadius,
      height,
      Math.sin(a0) * topRadius,
    ];
    const hi1: [number, number, number] = [
      Math.cos(a1) * topRadius,
      height,
      Math.sin(a1) * topRadius,
    ];
    // Outward winding: with +angle running counterclockwise seen from +Y and
    // a right-handed cross(v1-v0, v2-v0), (lo0, hi0, hi1) faces outward.
    faces.push([lo0, hi0, hi1], [lo0, hi1, lo1]);
  }
  return geometryFromFaces(faces);
}

/**
 * Extrudes a 2D cross-section profile (x,y points) along +Z into a curved
 * trough. Winding is chosen so the riding faces point toward the profile's
 * concave (inner) side — i.e. up and inward, the surfable band. Flat per-face
 * normals (non-indexed), like the other ramp factories.
 */
function pipeFromProfile(profile: [number, number][], length: number): BufferGeometry {
  const faces: [number, number, number][][] = [];
  for (let i = 0; i < profile.length - 1; i++) {
    const [x0, y0] = profile[i] as [number, number];
    const [x1, y1] = profile[i + 1] as [number, number];
    const a: [number, number, number] = [x0, y0, 0];
    const b: [number, number, number] = [x1, y1, 0];
    const c: [number, number, number] = [x1, y1, length];
    const d: [number, number, number] = [x0, y0, length];
    faces.push([a, c, b], [a, d, c]);
  }
  return geometryFromFaces(faces);
}

/**
 * Half-pipe: a circular U-trough (radius `radius`, opening upward) extruded
 * along +Z. Ride down its length and surf up either curved side wall; the
 * shallow centre is a walkable floor. `halfArcDeg` is the half-angle each wall
 * sweeps from the bottom (≈75° → near-vertical lips).
 */
export function makeHalfPipe({
  length,
  radius,
  halfArcDeg = 75,
  steps = 20,
}: {
  length: number;
  radius: number;
  halfArcDeg?: number;
  steps?: number;
}): BufferGeometry {
  const a = (halfArcDeg * Math.PI) / 180;
  const profile: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = -a + (2 * a * i) / steps;
    profile.push([radius * Math.sin(t), radius * (1 - Math.cos(t))]);
  }
  return pipeFromProfile(profile, length);
}

/**
 * Quarter-pipe: one curved wall (a quarter-trough) extruded along +Z — the
 * curved cousin of makeWallRamp. Ride along +Z and surf up the concave face.
 */
export function makeQuarterPipe({
  length,
  radius,
  arcDeg = 82,
  steps = 12,
}: {
  length: number;
  radius: number;
  arcDeg?: number;
  steps?: number;
}): BufferGeometry {
  const a = (arcDeg * Math.PI) / 180;
  const profile: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const t = (a * i) / steps;
    profile.push([radius * Math.sin(t), radius * (1 - Math.cos(t))]);
  }
  return pipeFromProfile(profile, length);
}

function geometryFromFaces(faces: [number, number, number][][]): BufferGeometry {
  const positions = new Float32Array(faces.flat().flat());
  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  generateBoxUVs(geometry);
  return geometry;
}

/**
 * World-scale box-projected UVs: each vertex is projected along its dominant
 * normal axis, with `scale` world units per texture tile. Works on any
 * geometry with position+normal (procedural prisms, glb meshes, planes).
 */
export function generateBoxUVs(geometry: BufferGeometry, scale = 256): void {
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  const uv = new Float32Array(position.count * 2);
  for (let i = 0; i < position.count; i++) {
    const nx = Math.abs(normal.getX(i));
    const ny = Math.abs(normal.getY(i));
    const nz = Math.abs(normal.getZ(i));
    let u: number;
    let v: number;
    if (ny >= nx && ny >= nz) {
      u = position.getX(i);
      v = position.getZ(i);
    } else if (nx >= nz) {
      u = position.getZ(i);
      v = position.getY(i);
    } else {
      u = position.getX(i);
      v = position.getY(i);
    }
    uv[i * 2] = u / scale;
    uv[i * 2 + 1] = v / scale;
  }
  geometry.setAttribute('uv', new BufferAttribute(uv, 2));
}
